import React, { useState, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";

import {
  Card,
  List,
  ListSubheader,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  DirectionsBus as DirectionsBusIcon,
  Bookmark as BookmarkIcon,
  EventAvailable as EventAvailableIcon,
  DateRange as DateRangeIcon,
  Chat as ChatIcon,
  LockOpen as LockOpenIcon,
  DeleteForever as DeleteForeverIcon,
} from "@material-ui/icons";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";

import { FirebaseAuth } from "services/firebase/Firebase";
import { StornierenIcon, PdfIcon } from "components";
import { AuthContext } from "context/Auth";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContents: {
      width: "-webkit-fill-available",
      paddingBottom: "11px",
    },
  })
);

interface IFormInput {
  currentPass: string;
  newPass: string;
  newPass2: string;
  email: string;
  pass: string;
  confirm: string;
}

export type SiderbarProps = {
  user: any;
  fade: any;
};

export const Sidebar: React.FC<SiderbarProps> = ({ user, fade }) => {
  const classes = useStyles();
  const history = useHistory();
  const { currentUser }: any = useContext(AuthContext);
  const { handleSubmit, register, errors, getValues, setError } = useForm<IFormInput>();

  const [selectedIndex, setSelectedIndex] = useState(1);
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [showChangePassDialog, setShowChangePassDialog] = useState(false);
  const [showAccountDeleteDialog, setShowAccountDeleteDialog] = useState(false);

  const signOut = () => {
    return FirebaseAuth.signOut()
      .then((_) => history.push("/"))
      .catch((reason) => console.log(reason));
  };

  const onSubmitToPasswordChangeForm: SubmitHandler<IFormInput> = (data) => {
    FirebaseAuth.signInWithEmailAndPassword(currentUser.email, data.currentPass)
      .then((_) => {
        if (FirebaseAuth.currentUser !== null) {
          FirebaseAuth.currentUser
            .updatePassword(data.newPass)
            .then(() => {
              setShowChangePassDialog(false);
              setShowSuccessAlert(true);
              setTimeout(() => {
                setShowSuccessAlert(false);
              }, 5000);
            })
            .catch((error) => {
              console.log(error);
              setShowChangePassDialog(false);
              setShowErrorAlert(true);
              setTimeout(() => {
                setShowErrorAlert(false);
              }, 5000);
            });
        }
      })
      .catch((reason) => {
        if (reason.code === "auth/wrong-password") {
          setError("currentPass", {
            types: {
              required: "This is required",
            },
          });
        }
        console.log(reason);
      });
  };

  const onSubmitToAccountDeleteForm: SubmitHandler<IFormInput> = (data) => {
    FirebaseAuth.signInWithEmailAndPassword(data.email, data.pass)
      .then((_) => {
        if (FirebaseAuth.currentUser !== null && data.confirm === "LÖSCHEN") {
          FirebaseAuth.currentUser
            .delete()
            .then(() => {
              setShowChangePassDialog(false);
              setShowSuccessAlert(true);
              setTimeout(() => {
                setShowSuccessAlert(false);
              }, 5000);
            })
            .catch((error) => {
              console.log(error);
              setShowChangePassDialog(false);
              setShowErrorAlert(true);
              setTimeout(() => {
                setShowErrorAlert(false);
              }, 5000);
            });
        }
      })
      .catch((reason) => {
        if (reason.code === "auth/wrong-password") {
          setError("pass", {
            types: {
              required: "This is required",
            },
          });
        }
        if (reason.code === "auth/wrong-email") {
          setError("email", {
            types: {
              required: "This is required",
            },
          });
        }
        console.log(reason);
      });
  };

  return (
    <>
      <Snackbar open={showSuccessAlert} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success">Ihr Passwort wurde erfolgreich übermittelt</Alert>
      </Snackbar>
      <Snackbar open={showErrorAlert} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error">Ihr Passwort wurde nicht erfolgreich übermittelt</Alert>
      </Snackbar>

      {/* Password Change Dialog */}
      <Dialog
        open={showChangePassDialog}
        onClose={() => setShowChangePassDialog(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Passwort ändern</DialogTitle>
        <form onSubmit={handleSubmit(onSubmitToPasswordChangeForm)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <TextField
                  name="currentPass"
                  variant="outlined"
                  label="Aktuelles Passwort"
                  required
                  type="password"
                  error={errors.currentPass ? true : false}
                  inputRef={register({ required: true, pattern: /(?=.*[0-9a-zA-Z]).{6,}/i })}
                  className={classes.formContents}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  name="newPass"
                  variant="outlined"
                  label="Neues Passwort"
                  type="password"
                  required
                  error={errors.newPass ? true : false}
                  inputRef={register({ required: true, pattern: /(?=.*[0-9a-zA-Z]).{6,}/i })}
                  className={classes.formContents}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  name="newPass2"
                  variant="outlined"
                  label="Neues Passwort"
                  type="password"
                  required
                  error={errors.newPass2 ? true : false}
                  inputRef={register({
                    required: true,
                    pattern: /(?=.*[0-9a-zA-Z]).{6,}/i,
                    validate: {
                      matchesPreviousPassword: (value) => {
                        const { newPass } = getValues();
                        return newPass === value;
                      },
                    },
                  })}
                  className={classes.formContents}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowChangePassDialog(false)} color="primary">
              Abbrechen
            </Button>
            <Button variant="contained" color="primary" disableElevation type="submit">
              Sichern
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* End Password Change Dialog */}

      {/* Account Delete Dialog */}
      <Dialog
        open={showAccountDeleteDialog}
        onClose={() => setShowAccountDeleteDialog(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Konto löschen</DialogTitle>

        <form onSubmit={handleSubmit(onSubmitToAccountDeleteForm)}>
          <DialogContentText style={{ marginLeft: "25px" }}>
            Bitte geben Sie den Text "LÖSCHEN" mit Großbuchstaben ein, um das Löschen Ihres Kontos zu
            bestätigen
          </DialogContentText>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <TextField
                  name="email"
                  variant="outlined"
                  label="E-mail"
                  required
                  error={!!errors.email}
                  inputRef={register({ required: true, pattern: /^\S+@\S+$/i })}
                  className={classes.formContents}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  name="pass"
                  variant="outlined"
                  label="Passwort"
                  type="password"
                  required
                  error={!!errors.pass}
                  inputRef={register({ required: true, pattern: /(?=.*[0-9a-zA-Z]).{6,}/i })}
                  className={classes.formContents}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  name="confirm"
                  variant="outlined"
                  label="Bestätigen."
                  required
                  error={!!errors.confirm}
                  inputRef={register({
                    required: true,
                    pattern: /[A-Z]/i,
                    validate: {
                      matchesPreviousPassword: (value) => "LÖSCHEN" === value,
                    },
                  })}
                  className={classes.formContents}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAccountDeleteDialog(false)} color="primary">
              Abbrechen
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "red", color: "white" }}
              disableElevation
              type="submit"
            >
              LÖSCHEN
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* End Account Delete Dialog */}

      {user != null && fade && (
        <Card style={{ marginBottom: 18 }}>
          <List subheader={<ListSubheader color="primary">Willkommen</ListSubheader>}>
            <ListItem>
              <ListItemAvatar>
                <Avatar
                  alt={user.person.firstName + " " + user.person.lastName}
                  src={user.profileImage.url}
                />
              </ListItemAvatar>
              <ListItemText primary={user.person.firstName + " " + user.person.lastName} />
            </ListItem>
            <ListItem button onClick={() => setShowChangePassDialog(true)}>
              <ListItemIcon>
                <LockOpenIcon />
              </ListItemIcon>
              <ListItemText primary="Passwort ändern" />
            </ListItem>
            <ListItem button onClick={() => setShowAccountDeleteDialog(true)}>
              <ListItemIcon>
                <DeleteForeverIcon />
              </ListItemIcon>
              <ListItemText primary="Konto löschen" />
            </ListItem>
            <ListItem button onClick={signOut}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Ausloggen" />
            </ListItem>
          </List>
        </Card>
      )}

      <Card>
        <List>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/profile", state: { user } }}
            onClick={() => setSelectedIndex(1)}
            selected={selectedIndex === 1}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/travellers", state: { user } }}
            onClick={() => setSelectedIndex(2)}
            selected={selectedIndex === 2}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Reisende" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/vehicles", state: { user } }}
            onClick={() => setSelectedIndex(3)}
            selected={selectedIndex === 3}
          >
            <ListItemIcon>
              <DirectionsBusIcon />
            </ListItemIcon>
            <ListItemText primary="Fahrzeuge" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/bookmarks", state: { user } }}
            onClick={() => setSelectedIndex(4)}
            selected={selectedIndex === 4}
          >
            <ListItemIcon>
              <BookmarkIcon />
            </ListItemIcon>
            <ListItemText primary="Favoriten" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/bookings", state: { user } }}
            onClick={() => setSelectedIndex(5)}
            selected={selectedIndex === 5}
          >
            <ListItemIcon>
              <EventAvailableIcon />
            </ListItemIcon>
            <ListItemText primary="Buchungen" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/bookings-past", state: { user } }}
            onClick={() => setSelectedIndex(6)}
            selected={selectedIndex === 6}
          >
            <ListItemIcon>
              <DateRangeIcon />
            </ListItemIcon>
            <ListItemText primary="Historie" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/reviews", state: { user } }}
            onClick={() => setSelectedIndex(7)}
            selected={selectedIndex === 7}
          >
            <ListItemIcon>
              <ChatIcon />
            </ListItemIcon>
            <ListItemText primary="Bewertungen" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/stornieren", state: { user } }}
            onClick={() => setSelectedIndex(9)}
            selected={selectedIndex === 9}
          >
            <ListItemIcon>
              <StornierenIcon />
            </ListItemIcon>
            <ListItemText primary="Stornieren" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to={{ pathname: "/user/rechnungen", state: { user } }}
            onClick={() => setSelectedIndex(10)}
            selected={selectedIndex === 10}
          >
            <ListItemIcon>
              <PdfIcon />
            </ListItemIcon>
            <ListItemText primary="Rechnungen" />
          </ListItem>
        </List>
      </Card>
    </>
  );
};
